// A DESTINATION'S FOLDERS, DOWNLOAD AND UPLOAD, IN THE MAIN ACC / PROCORE WINDOW (owner
// 2026-10-26: "i don't see rvt download path in main connection popup, i only see upload
// rvt path, same for nwc and nwd - i need download and upload for both platforms, procore
// and acc, just like shopdrawing upload, download").
//
// One grid per destination:            Download          Upload
//   Shop drawings                       folder            folder
//   Models - default (all types)        folder            folder
//   .rvt / .nwc / .nwd                  folder            folder
// A cell shows the folder in effect: the destination's own, else (for a type) its models
// default, else the company default - and says which. Saved through the folder slots
// (back/routes/folderPaths.js), which uploads, downloads and syncs already read.
import React, { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import addNotification from "helpers/notify";
import FolderPicker from "./FolderPicker";
import { getProjectFolderPaths, saveProjectFolderPaths } from "./folderPathsApi";
import { refreshIntegrationCards } from "./useIntegrations";

export const ROWS = [
  { key: "sd", label: "Shop drawings", down: ["shopDrawingsDownload"], up: ["shopDrawingsUpload"] },
  { key: "base", label: "Models - default (all types)", down: ["modelsDownload", "base"], up: ["modelsUpload", "base"] },
  { key: "rvt", label: "Revit (.rvt)", down: ["modelsDownload", "rvt"], up: ["modelsUpload", "rvt"] },
  { key: "nwc", label: "Navisworks (.nwc)", down: ["modelsDownload", "nwc"], up: ["modelsUpload", "nwc"] },
  { key: "nwd", label: "Navisworks (.nwd / .nwf)", down: ["modelsDownload", "nwd"], up: ["modelsUpload", "nwd"] },
];

// PICKED FILES, which RESTRICT a slot to themselves (owner, 2026-10-26: "make file
// restricts the sync and downlaod to that selected file, only"). They belong to the
// SLOT, not to a type - a file is already one type - so they get their own row at the
// foot of the grid rather than a cell per extension.
export const FILE_ROW = { key: "files", label: "Specific files", down: ["modelsDownload"], up: ["modelsUpload"] };

export const picksOf = (dest, slot) => {
  const own = dest && dest.own && dest.own[slot] && dest.own[slot].files;
  if (Array.isArray(own) && own.length) return { files: own, from: "own" };
  const eff = dest && dest.effective && dest.effective[slot] && dest.effective[slot].files;
  if (Array.isArray(eff) && eff.length) return { files: eff, from: "company" };
  return { files: [], from: "" };
};

const at = (paths, [slot, level]) => {
  const v = paths && paths[slot];
  return level ? (v && v[level]) || null : v || null;
};

/** The folder in effect and why: { own, used, why }. */
export function cellOf(dest, ref) {
  const own = dest ? at(dest.own, ref) : null;
  const eff = dest ? at(dest.effective, ref) : null;
  if (own) return { own, used: own, why: "" };
  if (!eff || !eff.target) return { own: null, used: null, why: "" };
  const why = eff.from === "company" ? "company default" : eff.level === "base" && ref[1] && ref[1] !== "base" ? "models default" : "";
  return { own: null, used: eff.target, why };
}

const startFrom = (t) => {
  if (!t || !t.accountId || !t.containerId) return null;
  const [accountName, containerName] = String(t.path || "").split(" / ");
  return { accountId: t.accountId, containerId: t.containerId, accountName, containerName };
};

export default function DestinationFolders({ projectId, provider, destination, connection, browseAs, canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(null); // { ref, label, start }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getProjectFolderPaths(projectId));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load, destination]);

  if (error) return <p className="text-danger">{error}</p>;
  if (!data) return <Spin size="small" />;

  // this destination - or, before it has a row, the account it will use
  const accounts = (data.accounts || []).filter((a) => a.provider === provider);
  const account = accounts.find((a) => (a.destinations || []).some((d) => d.destination === destination))
    || accounts.find((a) => (connection ? a.connection === connection : a.primary)) || null;
  const dest = account && ((account.destinations || []).find((d) => d.destination === destination)
    || (!destination ? { own: null, effective: account.effective } : null));
  const editable = canEdit && !!account && !!(data.canEdit && data.canEdit[provider]);

  const save = async (ref, target, label) => {
    const [slot, level] = ref;
    const paths = { [slot]: level ? { [level]: target } : target };
    setSaving(true);
    try {
      await saveProjectFolderPaths(projectId, provider, { ...(destination ? { destination } : { connectionId: account.connection }), paths });
      addNotification(target ? `${label}: ${target.path}` : `${label} cleared`, "success");
      setPicking(null);
      await load();
      refreshIntegrationCards();
      if (onChanged) onChanged();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  // Picks are saved as the WHOLE list for that slot, so removing one is the same
  // call as adding one and there is no partial state to get wrong.
  const saveFiles = async (slot, files, what) => {
    setSaving(true);
    try {
      await saveProjectFolderPaths(projectId, provider, { ...(destination ? { destination } : { connectionId: account.connection }), paths: { [slot]: { files } } });
      addNotification(what, "success");
      setPicking(null);
      await load();
      refreshIntegrationCards();
      if (onChanged) onChanged();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  const filesCell = (dir) => {
    const slot = dir === "down" ? FILE_ROW.down[0] : FILE_ROW.up[0];
    const { files, from } = picksOf(dest, slot);
    const mine = from === "own";
    return (
      <td className="dest-folders-cell" data-testid={`dest-files-${dir}`}>
        {files.length ? (
          <>
            {files.map((f) => (
              <span key={`${f.containerId}-${f.itemId}`} className="d-block">
                <span className={mine ? "integration-path" : "integration-path text-muted"} title={f.path}>{f.name}</span>
                {editable && mine ? (
                  <button
                    className="integration-link-btn danger"
                    disabled={saving}
                    onClick={() => saveFiles(slot, files.filter((x) => !(x.containerId === f.containerId && x.itemId === f.itemId)), `${f.name} removed`)}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            ))}
            {from === "company" ? <small className="text-muted d-block">(company default)</small> : null}
            <small className="text-muted d-block">
              {dir === "down"
                ? "Only these files are synced - the folder above is not read."
                : "Uploads go into this file's folder; a file of the same name becomes a new version."}
            </small>
          </>
        ) : (
          <>
            <span className="text-muted">Whole folder</span>
            <small className="text-muted d-block">
              {dir === "down" ? "Everything in the folders above." : "Uploads go to the folders above."}
            </small>
          </>
        )}
        {editable ? (
          <span className="d-block">
            <button
              className="integration-link-btn"
              style={{ marginLeft: 0 }}
              disabled={saving}
              onClick={() => setPicking({
                files: true,
                slot,
                current: mine ? files : [],
                label: `${FILE_ROW.label} - ${dir === "down" ? "download" : "upload"}`,
                start: startFrom((files[0] && { accountId: files[0].accountId, containerId: files[0].containerId, path: files[0].path })
                  || cellOf(dest, dir === "down" ? ["modelsDownload", "base"] : ["modelsUpload", "base"]).used),
              })}
            >
              Add a file
            </button>
            {mine ? (
              <button className="integration-link-btn danger" disabled={saving} onClick={() => saveFiles(slot, [], "Back to the whole folder")}>
                Clear
              </button>
            ) : null}
          </span>
        ) : null}
      </td>
    );
  };

  if (picking) {
    return (
      <div className="integration-section" data-testid="dest-folders-picking">
        <p className="mb-2"><b>{picking.label}: open the folder</b></p>
        <FolderPicker
          provider={provider}
          connection={browseAs}
          mode={picking.files ? "file" : "folder"}
          start={picking.start}
          busy={saving}
          onCancel={() => setPicking(null)}
          onPick={(t) => (picking.files
            ? saveFiles(
              picking.slot,
              // ADDS to the list rather than replacing it, so picking three files is
              // three clicks and not three rounds of losing the previous one.
              [...picking.current.filter((x) => !(x.containerId === t.containerId && x.itemId === t.itemId)),
                { accountId: t.accountId, containerId: t.containerId, folderId: t.folderId, itemId: t.itemId, name: t.name, path: t.path }],
              `${t.name} added`,
            )
            : save(picking.ref, { accountId: t.accountId, containerId: t.containerId, folderId: t.folderId, path: t.path }, picking.label))}
        />
      </div>
    );
  }

  const cell = (row, dir) => {
    const ref = dir === "down" ? row.down : row.up;
    const c = cellOf(dest, ref);
    const label = `${row.label} - ${dir === "down" ? "download" : "upload"}`;
    return (
      <td className="dest-folders-cell" data-testid={`dest-folder-${row.key}-${dir}`}>
        {c.used ? (
          <span className={c.own ? "integration-path" : "integration-path text-muted"} title={c.used.path}>{c.used.path}</span>
        ) : (
          <span className="text-muted">Not set</span>
        )}
        {c.why ? <small className="text-muted d-block">({c.why})</small> : null}
        {editable ? (
          <span className="d-block">
            <button className="integration-link-btn" style={{ marginLeft: 0 }} disabled={saving}
              onClick={() => setPicking({ ref, label, start: startFrom(c.used) })}>
              {c.own ? "Change" : "Choose"}
            </button>
            {c.own ? (
              <button className="integration-link-btn danger" disabled={saving} onClick={() => save(ref, null, label)}>Clear</button>
            ) : null}
          </span>
        ) : null}
      </td>
    );
  };

  return (
    <div className="integration-section" data-testid="dest-folders">
      <div className="integration-section-title">Folders - download and upload</div>
      {!account ? (
        <p className="text-muted mb-1">Choose the account for this destination first.</p>
      ) : (
        <table className="dest-folders">
          <thead>
            <tr><th /><th>Download (into BidLight)</th><th>Upload (from BidLight)</th></tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {cell(row, "down")}
                {cell(row, "up")}
              </tr>
            ))}
            {/* LAST, because it OVERRIDES the folders above it rather than adding
                to them - a reader should meet the folders first and then the thing
                that narrows them. */}
            <tr key={FILE_ROW.key} data-testid="dest-folders-files-row">
              <th scope="row">
                {FILE_ROW.label}
                <small className="text-muted d-block">instead of the folders above</small>
              </th>
              {filesCell("down")}
              {filesCell("up")}
            </tr>
          </tbody>
        </table>
      )}
      <small className="text-muted d-block">
        A type left empty uses the models default; a folder left empty uses the company default (Profile, Procore / ACC folders).
        Model Files sync and Export to 3D read the download folders; Send to, sync uploads and approved versions go to the upload folders.
      </small>
    </div>
  );
}
